// Connect-data-source wizard (connection manager slice C4).
//
// The "+ Connect data source…" affordance on SessionPanel opens this
// modal. Flow (design note §5):
//
//   engine picker → connect form (path) → lazy object-explorer tree
//   (Connection ▸ Database ▸ Schema ▸ Relation ▸ Column) → top-N
//   preview pane → "Create data source".
//
// A connection is transient and spans many databases; the user browses
// it, then materialises ONE chosen database into a persistent data
// source (installation). On success we hand the created descriptor back
// so SessionPanel can refresh the dropdown and auto-select it.

import { useEffect, useState } from "react";
import { ApiError, type CoframeApiClient } from "../api/client";
import type {
  ConnPreviewResponse,
  ConnTreeNode,
  ConnectResponse,
  CreateDataSourceResponse,
} from "../api/client";

interface Props {
  client: CoframeApiClient;
  onClose: () => void;
  onCreated: (resp: CreateDataSourceResponse) => void;
}

export function ConnectDataSourceModal({ client, onClose, onCreated }: Props) {
  // ── connect form ──
  const [engines, setEngines] = useState<string[]>([]);
  const [engine, setEngine] = useState<string>("");
  const [path, setPath] = useState<string>(".");
  const [connecting, setConnecting] = useState(false);

  // ── live connection + lazy tree ──
  const [connection, setConnection] = useState<ConnectResponse | null>(null);
  const [childrenByRef, setChildrenByRef] = useState<
    Record<string, ConnTreeNode[]>
  >({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingRef, setLoadingRef] = useState<string | null>(null);

  // ── selection + preview ──
  // The database (a *.duckdb / *.db / *.parquet file name) the user chose
  // to materialise. Set when a database-kind node is clicked.
  const [selectedDatabase, setSelectedDatabase] = useState<string | null>(null);
  const [previewRef, setPreviewRef] = useState<string | null>(null);
  const [preview, setPreview] = useState<ConnPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── create form ──
  const [name, setName] = useState<string>("");
  const [holdOff, setHoldOff] = useState<number>(0);
  const [creating, setCreating] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Load the engine list once; default to the first.
  useEffect(() => {
    client
      .listConnectors()
      .then((r) => {
        setEngines(r.engines);
        if (r.engines.length > 0) setEngine(r.engines[0]!);
      })
      .catch((e: unknown) => setError(formatError(e)));
  }, [client]);

  async function handleConnect() {
    if (engine === "") return;
    setConnecting(true);
    setError(null);
    setConnection(null);
    setChildrenByRef({});
    setExpanded(new Set());
    setSelectedDatabase(null);
    setPreview(null);
    setPreviewRef(null);
    try {
      const resp = await client.openConnection({ engine, path });
      setConnection(resp);
      // Auto-expand the root so the user sees databases immediately.
      await loadChildren(resp.connection_id, resp.root.ref);
      setExpanded(new Set([resp.root.ref]));
    } catch (e) {
      setError(formatError(e));
    } finally {
      setConnecting(false);
    }
  }

  async function loadChildren(connectionId: string, ref: string) {
    setLoadingRef(ref);
    try {
      const r = await client.getConnectionChildren(connectionId, ref);
      setChildrenByRef((prev) => ({ ...prev, [ref]: r.children }));
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoadingRef(null);
    }
  }

  async function toggleNode(node: ConnTreeNode) {
    if (connection === null) return;
    const isOpen = expanded.has(node.ref);
    if (isOpen) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(node.ref);
        return next;
      });
      return;
    }
    if (childrenByRef[node.ref] === undefined) {
      await loadChildren(connection.connection_id, node.ref);
    }
    setExpanded((prev) => new Set(prev).add(node.ref));
  }

  async function handleSelect(node: ConnTreeNode) {
    if (connection === null) return;
    setError(null);
    if (node.kind === "database") {
      setSelectedDatabase(node.name);
      // Default the new source name from the database stem.
      setName(stem(node.name));
    }
    if (node.kind === "relation") {
      setPreviewRef(node.ref);
      setPreview(null);
      setPreviewLoading(true);
      try {
        const r = await client.getConnectionPreview(
          connection.connection_id,
          node.ref,
          { n: 50 },
        );
        setPreview(r);
      } catch (e) {
        setError(formatError(e));
      } finally {
        setPreviewLoading(false);
      }
    }
  }

  async function handleCreate() {
    if (connection === null || selectedDatabase === null) return;
    setCreating(true);
    setError(null);
    try {
      const resp = await client.createDataSource({
        connection_id: connection.connection_id,
        database: selectedDatabase,
        name: name.trim() === "" ? null : name.trim(),
        default_hold_off_days: holdOff,
      });
      onCreated(resp);
    } catch (e) {
      setError(formatError(e));
      setCreating(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerRowStyle}>
          <h2 style={{ margin: 0 }}>Connect a data source</h2>
          <button onClick={onClose} style={closeBtnStyle} title="Close">
            ×
          </button>
        </div>
        <p style={subtitleStyle}>
          Pick an engine, point it at a directory or file, browse what's
          there, then pin one database as a data source.
        </p>

        {/* ── connect form ── */}
        <div style={connectRowStyle}>
          <label style={labelStyle}>
            engine
            <select
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              style={inputStyle}
            >
              {engines.length === 0 && <option value="">— none —</option>}
              {engines.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: 1 }}>
            path (directory or file, relative to the connection root)
            <input
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="."
              style={{ ...inputStyle, minWidth: "16rem", width: "100%" }}
            />
          </label>
          <button
            onClick={handleConnect}
            disabled={connecting || engine === ""}
            style={primaryBtnStyle}
          >
            {connecting ? "Connecting…" : "Connect"}
          </button>
        </div>

        {error && (
          <div style={errorBlockStyle}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* ── explorer + preview ── */}
        {connection !== null && (
          <div style={bodyGridStyle}>
            <div style={treePaneStyle}>
              <div style={paneHeadStyle}>Object explorer</div>
              <TreeNodeRow
                node={connection.root}
                depth={0}
                expanded={expanded}
                childrenByRef={childrenByRef}
                loadingRef={loadingRef}
                selectedDatabase={selectedDatabase}
                previewRef={previewRef}
                onToggle={toggleNode}
                onSelect={handleSelect}
              />
            </div>

            <div style={previewPaneStyle}>
              <div style={paneHeadStyle}>Preview</div>
              {previewLoading && <div style={mutedStyle}>Loading preview…</div>}
              {!previewLoading && preview === null && (
                <div style={mutedStyle}>
                  Select a relation (table / view) to preview its rows.
                </div>
              )}
              {!previewLoading && preview !== null && (
                <PreviewTable preview={preview} />
              )}
            </div>
          </div>
        )}

        {/* ── create ── */}
        {connection !== null && (
          <div style={createRowStyle}>
            <div style={{ flex: 1 }}>
              {selectedDatabase === null ? (
                <span style={mutedStyle}>
                  Click a <strong>database</strong> node in the tree to pick
                  what to materialise.
                </span>
              ) : (
                <span style={{ fontSize: "0.85rem" }}>
                  Materialising <code>{selectedDatabase}</code> as a data
                  source.
                </span>
              )}
            </div>
            <label style={labelStyle}>
              name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="data-source-name"
                style={{ ...inputStyle, minWidth: "12rem" }}
                disabled={selectedDatabase === null}
              />
            </label>
            <label style={labelStyle}>
              hold-off days
              <input
                type="number"
                min={0}
                value={holdOff}
                onChange={(e) =>
                  setHoldOff(Math.max(0, Number(e.target.value) || 0))
                }
                style={{ ...inputStyle, minWidth: "5rem", width: "5rem" }}
                disabled={selectedDatabase === null}
              />
            </label>
            <button
              onClick={handleCreate}
              disabled={creating || selectedDatabase === null}
              style={primaryBtnStyle}
            >
              {creating ? "Creating…" : "Create data source"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── tree row (recursive) ──────────────────────────────────────────────

interface RowProps {
  node: ConnTreeNode;
  depth: number;
  expanded: Set<string>;
  childrenByRef: Record<string, ConnTreeNode[]>;
  loadingRef: string | null;
  selectedDatabase: string | null;
  previewRef: string | null;
  onToggle: (node: ConnTreeNode) => void;
  onSelect: (node: ConnTreeNode) => void;
}

function TreeNodeRow(props: RowProps) {
  const {
    node,
    depth,
    expanded,
    childrenByRef,
    loadingRef,
    selectedDatabase,
    previewRef,
    onToggle,
    onSelect,
  } = props;
  const isOpen = expanded.has(node.ref);
  const kids = childrenByRef[node.ref];
  const isSelectedDb =
    node.kind === "database" && node.name === selectedDatabase;
  const isPreviewed = node.ref === previewRef;
  const clickable = node.kind === "database" || node.kind === "relation";

  return (
    <div>
      <div
        style={{
          ...rowStyle,
          paddingLeft: 6 + depth * 16,
          background: isSelectedDb || isPreviewed ? "#eef4ff" : undefined,
        }}
      >
        {node.has_children ? (
          <button
            onClick={() => onToggle(node)}
            style={twistyStyle}
            title={isOpen ? "Collapse" : "Expand"}
          >
            {loadingRef === node.ref ? "…" : isOpen ? "▾" : "▸"}
          </button>
        ) : (
          <span style={{ ...twistyStyle, cursor: "default" }} />
        )}
        <span style={kindBadgeStyle}>{node.kind[0]!.toUpperCase()}</span>
        <span
          onClick={clickable ? () => onSelect(node) : undefined}
          style={{
            cursor: clickable ? "pointer" : "default",
            fontFamily: "monospace",
            fontSize: "0.85rem",
            fontWeight: isSelectedDb ? 600 : 400,
          }}
          title={clickable ? `Select ${node.name}` : undefined}
        >
          {node.name}
        </span>
        {node.detail && <span style={detailStyle}>{node.detail}</span>}
      </div>
      {isOpen && kids !== undefined && (
        <div>
          {kids.length === 0 && (
            <div
              style={{
                ...mutedStyle,
                paddingLeft: 6 + (depth + 1) * 16,
                fontSize: "0.78rem",
              }}
            >
              (empty)
            </div>
          )}
          {kids.map((child) => (
            <TreeNodeRow key={child.ref} {...props} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewTable({ preview }: { preview: ConnPreviewResponse }) {
  return (
    <div style={{ overflow: "auto", maxHeight: "16rem" }}>
      <table style={previewTableStyle}>
        <thead>
          <tr>
            {preview.columns.map((c) => (
              <th key={c} style={thStyle}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} style={tdStyle}>
                  {cell === null ? <em style={{ color: "#aaa" }}>null</em> : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...mutedStyle, marginTop: 4, fontSize: "0.78rem" }}>
        {preview.rows.length} row(s){preview.truncated ? " · truncated" : ""}
      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────

function stem(filename: string): string {
  const slash = filename.lastIndexOf("/");
  const base = slash >= 0 ? filename.slice(slash + 1) : filename;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(0, dot) : base;
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── styles ────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: "4vh",
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 8,
  padding: "1.25rem 1.5rem",
  width: "min(56rem, 92vw)",
  maxHeight: "88vh",
  overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
};
const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
const closeBtnStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  fontSize: "1.5rem",
  lineHeight: 1,
  cursor: "pointer",
  color: "#666",
};
const subtitleStyle: React.CSSProperties = {
  color: "#666",
  fontSize: "0.88rem",
  marginTop: "0.25rem",
};
const connectRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.75rem",
  alignItems: "flex-end",
  marginBottom: "0.75rem",
};
const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: "0.8rem",
  color: "#444",
  gap: "0.2rem",
};
const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  fontSize: "0.9rem",
  border: "1px solid #ccc",
  borderRadius: 4,
};
const primaryBtnStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "#0366d6",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.9rem",
  whiteSpace: "nowrap",
};
const bodyGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "0.75rem",
  marginTop: "0.5rem",
};
const treePaneStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  borderRadius: 4,
  padding: "0.4rem",
  minHeight: "12rem",
  maxHeight: "22rem",
  overflow: "auto",
};
const previewPaneStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  borderRadius: 4,
  padding: "0.5rem",
  minHeight: "12rem",
  maxHeight: "22rem",
  overflow: "auto",
};
const paneHeadStyle: React.CSSProperties = {
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#888",
  marginBottom: "0.4rem",
};
const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.1rem 0",
};
const twistyStyle: React.CSSProperties = {
  width: "1.1rem",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#666",
  fontSize: "0.8rem",
  padding: 0,
};
const kindBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  width: "1rem",
  textAlign: "center",
  fontSize: "0.65rem",
  color: "#fff",
  background: "#9aa5b1",
  borderRadius: 3,
};
const detailStyle: React.CSSProperties = {
  fontSize: "0.72rem",
  color: "#999",
  marginLeft: "0.35rem",
};
const createRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.75rem",
  alignItems: "flex-end",
  marginTop: "0.9rem",
  paddingTop: "0.75rem",
  borderTop: "1px solid #eee",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.5rem 0.7rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  margin: "0.5rem 0",
  fontSize: "0.85rem",
};
const mutedStyle: React.CSSProperties = {
  color: "#888",
  fontSize: "0.85rem",
};
const previewTableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  fontSize: "0.78rem",
  fontFamily: "monospace",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.2rem 0.5rem",
  borderBottom: "2px solid #ddd",
  position: "sticky",
  top: 0,
  background: "#fafafa",
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "0.15rem 0.5rem",
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};
